import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';

import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import CancelOutlined from '@mui/icons-material/CancelOutlined';

import useOrderReport from 'hooks/reports/useOrderReport';
import ReportHeader from 'components/reports/ReportHeader';
import ReportFilters from 'components/reports/ReportFilters';
import ReportSummaryCards from 'components/reports/ReportSummaryCards';
import ReportTable from 'components/reports/ReportTable';
import { ReportBarChart } from 'components/reports/ReportCharts';
import ExportActions from 'components/reports/ExportActions';
import EmptyState from 'components/reports/EmptyState';
import LoadingState from 'components/reports/LoadingState';

import { useCurrency } from 'contexts/CurrencyContext';
import { formatCurrency } from 'utils/currency';
import { orderStatusChipSx, normalizeOrderStatusKey } from 'components/table/tableChipStyles';

const getErrorMessage = (error) => {
  if (!error) return '';
  const data = error.response?.data;
  if (data) {
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return `${data.message}: ${data.errors.join(', ')}`;
    }
    return data.message || data.error || error.message;
  }
  return error.message;
};

export default function OrderReport() {
  const { currency } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL search params
  const startDate = searchParams.get('startDate') || dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const endDate = searchParams.get('endDate') || dayjs().format('YYYY-MM-DD');
  const storeId = searchParams.get('storeId') || '';
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sortBy') || 'timestamp';
  const sortOrder = searchParams.get('sortOrder') || 'DESC';

  const filters = { startDate, endDate, storeId, search, status, page, sortBy, sortOrder };

  // Fetch report data
  const { data, isLoading, error, refetch } = useOrderReport({
    startDate,
    endDate,
    storeId,
    search,
    page,
    limit: 10,
    sortBy,
    sortOrder
  });

  const handleFilterChange = (newFilters) => {
    const nextParams = {};
    Object.keys(newFilters).forEach((key) => {
      if (newFilters[key] !== undefined && newFilters[key] !== '') {
        nextParams[key] = String(newFilters[key]);
      }
    });
    setSearchParams(nextParams);
  };

  const handlePaginationModelChange = (model) => {
    handleFilterChange({
      ...filters,
      page: model.page + 1
    });
  };

  const handleSortModelChange = (sortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      handleFilterChange({
        ...filters,
        sortBy: field,
        sortOrder: sort.toUpperCase(),
        page: 1
      });
    } else {
      handleFilterChange({
        ...filters,
        sortBy: 'timestamp',
        sortOrder: 'DESC',
        page: 1
      });
    }
  };

  const responseData = data?.data || {};
  const summary = responseData.summary || {};
  const statusCounts = summary.status_counts || {};
  const byStore = responseData.by_store || [];
  const byDeliveryBoy = responseData.by_delivery_boy || [];
  const listing = responseData.listing || [];
  const pagination = responseData.pagination || {};

  // Client-side filtering of listing based on status dropdown
  const filteredListing = status ? listing.filter((o) => String(o.order_status).toLowerCase() === status.toLowerCase()) : listing;

  // Summary Cards Configuration
  const summaryCards = [
    {
      label: 'Completed Orders',
      value: Number(statusCounts.completed || 0).toLocaleString(),
      tooltip: 'Completed / Served orders',
      icon: CheckCircleOutlineOutlined,
      color: '#359163'
    },
    {
      label: 'Pending Orders',
      value: Number(statusCounts.pending || 0).toLocaleString(),
      tooltip: 'Pending, accepted, cooking, or out for delivery',
      icon: AccessTimeOutlined,
      color: '#d4920a'
    },
    {
      label: 'Delivered Orders',
      value: Number(statusCounts.delivered || 0).toLocaleString(),
      tooltip: 'Delivered orders count',
      icon: LocalShippingOutlined,
      color: '#133a28'
    },
    {
      label: 'Cancelled Orders',
      value: Number(statusCounts.cancelled || 0).toLocaleString(),
      tooltip: 'Cancelled or rejected orders',
      icon: CancelOutlined,
      color: '#b71c1c'
    }
  ];

  // Table Columns
  const tableColumns = [
    { field: 'order_no', headerName: 'Order No', flex: 1, minWidth: 120, sortable: false },
    {
      field: 'Store',
      headerName: 'Store',
      flex: 1.3,
      minWidth: 160,
      sortable: false,
      valueGetter: (value, row) => row.Store?.title_en || '—'
    },
    {
      field: 'DeliveryBoy',
      headerName: 'Driver',
      flex: 1.3,
      minWidth: 160,
      sortable: false,
      valueGetter: (value, row) => row.DeliveryBoy?.name || '—'
    },
    {
      field: 'Customer',
      headerName: 'Customer',
      flex: 1.3,
      minWidth: 160,
      sortable: false,
      valueGetter: (value, row) => row.Customer?.name || '—'
    },
    {
      field: 'order_status',
      headerName: 'Status',
      flex: 1.2,
      minWidth: 130,
      renderCell: (params) => <Chip label={params.value} size="small" sx={orderStatusChipSx(normalizeOrderStatusKey(params.value))} />
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1.1,
      minWidth: 120,
      valueFormatter: (params) => formatCurrency(params.value || 0, currency.code, currency.symbol)
    },
    {
      field: 'timestamp',
      headerName: 'Date',
      flex: 1.2,
      minWidth: 150,
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString() : '')
    }
  ];

  const paginationModel = {
    page: page - 1,
    pageSize: 10
  };

  return (
    <Box sx={{ pb: 3 }}>
      <ReportHeader
        title="Order Report"
        subtitle="Track order metrics, delivery boys, completion rates, cancelled volumes, and store ordering patterns."
        actions={<ExportActions data={filteredListing} columns={tableColumns} title="Order Report" />}
      />

      <ReportFilters filters={filters} onFilterChange={handleFilterChange} />

      {isLoading ? (
        <LoadingState cardCount={5} />
      ) : error ? (
        <EmptyState type="error" message={getErrorMessage(error)} onRetry={refetch} />
      ) : (
        <Box sx={{ animation: 'fadeIn 0.3s ease-out' }}>
          <ReportSummaryCards cards={summaryCards} />

          {listing.length > 0 && (
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {/* Orders by Store Bar Chart */}
              <Grid size={{ xs: 12, md: 6 }}>
                <ReportBarChart data={byStore} xKey="store_name" yKey="order_count" name="Orders" title="Orders By Store" />
              </Grid>

              {/* Sales by Store Bar Chart */}
              <Grid size={{ xs: 12, md: 6 }}>
                <ReportBarChart data={byStore} xKey="store_name" yKey="total_sales" name="Sales" title="Sales By Store" isCurrency />
              </Grid>

              {/* Orders by Driver Bar Chart */}
              <Grid size={{ xs: 12, md: 6 }}>
                <ReportBarChart data={byDeliveryBoy} xKey="delivery_boy_name" yKey="order_count" name="Orders" title="Orders By Driver" />
              </Grid>

              {/* Sales by Driver Bar Chart */}
              <Grid size={{ xs: 12, md: 6 }}>
                <ReportBarChart
                  data={byDeliveryBoy}
                  xKey="delivery_boy_name"
                  yKey="total_sales"
                  name="Sales"
                  title="Sales By Driver"
                  isCurrency
                />
              </Grid>
            </Grid>
          )}

          {filteredListing.length === 0 ? (
            <EmptyState
              type={search || storeId || status ? 'nofilters' : 'nodata'}
              message={search || storeId || status ? undefined : 'No report data available'}
            />
          ) : (
            <ReportTable
              columns={tableColumns}
              rows={filteredListing}
              loading={isLoading}
              rowCount={pagination.total || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              onSortModelChange={handleSortModelChange}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
